/**
 * DXF HEADER persistence for AutoCAD linetype-scale system variables (PDF-6.1).
 *
 * AutoCAD writes the global / per-entity / space linetype-scale sysvars into
 * the HEADER section as alternating `9 $NAME` + `<code> <value>` pairs:
 *
 *   $LTSCALE    — float (group 40) — global linetype scale (default 1)
 *   $CELTSCALE  — float (group 40) — current per-object linetype scale for
 *                                    NEWLY created entities (default 1).
 *                                    NOTE: this is the *drawing-level current*
 *                                    value; the per-entity stored value is DXF
 *                                    group 48 on each entity, handled by the
 *                                    entity parser.
 *   $PSLTSCALE  — int16 (group 70) — paper-space linetype scaling flag
 *                                    (1 = scale by paper-space units, default).
 *   $MSLTSCALE  — int16 (group 70) — model-space annotative linetype scaling
 *                                    flag (R2007+, default 1).
 *
 * Read/write/patch mechanics are shared with the document-level vars via
 * `headerVarCodec.ts` (the generic numeric HEADER-var codec); this module owns
 * only the typed object and the var-table. The dash/linetype renderer
 * (`computeEffectiveDash`) consumes the decoded values via `LinetypeSysvars`
 * so dashed lines plot at the authored scale.
 *
 * AutoCAD reference: drawing system-variable reference 2018+.
 */

import {
  patchNumericVarsIntoHeaderGroups,
  readNumericVarsFromHeader,
  writeNumericVarsToHeaderLines,
  type HeaderVarSpec,
} from "./headerVarCodec";
import type { DxfGroup } from "./types";

// ── Public types ─────────────────────────────────────────────────────────

/**
 * Decoded values for the linetype-scale HEADER variables. Every field is
 * optional so a legacy DXF without them yields an empty object — the bridge
 * layer applies the schema defaults (`ltscale: 1`, `psltscale: 1`,
 * `msltscale: 1`, `celtscale: 1`).
 */
export type HeaderLinetypeVars = {
  /** $LTSCALE — group 40 — global linetype scale. */
  ltscale?: number;
  /** $CELTSCALE — group 40 — current (new-object) per-object linetype scale. */
  celtscale?: number;
  /** $PSLTSCALE — group 70 — paper-space linetype scaling flag (0/1). */
  psltscale?: number;
  /** $MSLTSCALE — group 70 — model-space annotative linetype scaling flag (0/1). */
  msltscale?: number;
};

/**
 * Var table: field key on {@link HeaderLinetypeVars} for each `$NAME`, plus the
 * DXF group code that carries its value. $LTSCALE / $CELTSCALE are floats
 * (group 40); $PSLTSCALE / $MSLTSCALE are int16 flags (group 70). Drives the
 * generic reader / writer / patch so the encoding stays symmetric.
 */
const VAR_FIELDS: ReadonlyArray<HeaderVarSpec<HeaderLinetypeVars>> = [
  { name: "$LTSCALE", field: "ltscale", code: 40, int: false },
  { name: "$CELTSCALE", field: "celtscale", code: 40, int: false },
  { name: "$PSLTSCALE", field: "psltscale", code: 70, int: true },
  { name: "$MSLTSCALE", field: "msltscale", code: 70, int: true },
];

// ── Read ─────────────────────────────────────────────────────────────────

/**
 * Scan a HEADER section's flat group list and extract the linetype-scale
 * sysvars. Delegates to the shared numeric-var codec; unknown variables and
 * name matches with an unexpected value code are ignored.
 */
export function readLinetypeVarsFromHeader(
  headerGroups: ReadonlyArray<DxfGroup>,
): HeaderLinetypeVars {
  return readNumericVarsFromHeader(headerGroups, VAR_FIELDS);
}

// ── Write ────────────────────────────────────────────────────────────────

/**
 * Produce the DxfGroup pairs for the linetype-scale HEADER variables.
 * Symmetric with `readLinetypeVarsFromHeader` — round-trip safe. Only fields
 * present on the input are emitted.
 */
export function writeLinetypeVarsToHeaderLines(
  vars: HeaderLinetypeVars,
): DxfGroup[] {
  return writeNumericVarsToHeaderLines(vars, VAR_FIELDS);
}

// ── Patch ────────────────────────────────────────────────────────────────

/**
 * Splice linetype-scale variable entries into an existing HEADER section group
 * array: replace the value group following each matching `9 $NAME` entry, then
 * append any vars absent from the original section (canonical order). Every
 * other HEADER variable is preserved verbatim. Used by the DXF export path
 * (`exportDrawingDocumentToDxfDocument`) to persist
 * `doc.ltscale/celtscale/psltscale/msltscale` even when the typed values were
 * edited after import (or no raw HEADER pre-existed).
 */
export function patchLinetypeVarsIntoHeaderGroups(
  existingGroups: ReadonlyArray<DxfGroup>,
  vars: HeaderLinetypeVars,
): DxfGroup[] {
  return patchNumericVarsIntoHeaderGroups(existingGroups, vars, VAR_FIELDS);
}
